import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import React, { ReactNode } from "react";
import { AuthProvider } from "react-oidc-context";
import { getCookie } from "shared-utils/src/cookies";

/**
 * D3 (Deltatre) OIDC Authentication Provider for SPL
 *
 * Manages OIDC authentication with D3 endpoints:
 * - Uses D3 OIDC endpoints for authentication
 * - No automatic token renewal (users login explicitly)
 * - Simpler scope (just "openid")
 * - Maintains app webview compatibility
 *
 * ERROR HANDLING STRATEGY:
 * - During auth flow (/auth/callback, /player/logged-in): Allow errors to be displayed by components
 * - After successful auth: Redirect to homepage on 401/403 (session expired)
 * - This ensures users see helpful error messages during login failures
 */

// Flag to track if we're currently in the authentication flow
// This prevents the fetch interceptor from redirecting during initial login
// allowing error messages to be displayed to the user
let isInAuthFlow = false;

export const setAuthFlowActive = (active: boolean) => {
  isInAuthFlow = active;
};

// Set by the apps in a webview to facilitate seamless login
const appCookieName = "ACCESS_TOKEN";

// Get cookie domain from current hostname
// e.g., react-fe.spl-dev.deltatre.digital -> .spl-dev.deltatre.digital
//       en.spl.app -> .spl.app
const getAppCookieDomain = (): string => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  // For subdomains, use parent domain with leading dot
  // e.g., react-fe.spl-dev.deltatre.digital -> .spl-dev.deltatre.digital
  if (parts.length > 2) {
    return "." + parts.slice(-3).join(".");
  }
  // For top-level domains, use with leading dot
  // e.g., spl.app -> .spl.app
  return "." + hostname;
};

const userManager = new UserManager({
  authority: import.meta.env.VITE_D3_AUTHORITY,
  client_id: import.meta.env.VITE_D3_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback/`,
  scope: "openid", // D3 only requires openid scope
  response_type: "code", // Authorization code flow
  response_mode: "fragment", // D3 returns in hash fragment
  post_logout_redirect_uri: `${window.location.origin}/`,
  automaticSilentRenew: false, // No automatic refresh - users login explicitly
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Disable automatic token exchange - we'll handle it via our backend
  loadUserInfo: false,
});

export const getD3AuthDomain = (): string => {
  return userManager.settings.authority || "";
};

const isResourceOurAPI = (resource: RequestInfo | URL) => {
  const resourceUrl =
    resource instanceof Request ? resource.url : resource.toString();

  try {
    const url = new URL(resourceUrl, window.location.origin);
    // Exact origin match or relative URL starting with /api/
    return (
      url.origin === window.location.origin && url.pathname.startsWith("/api/")
    );
  } catch {
    // Invalid URL, not our API
    return false;
  }
};

const handleAuthIssue = async (errorContext?: {
  status: number;
  url: string;
  response?: Response;
}) => {
  // Don't redirect if we're in the auth flow - let components show error messages
  if (isInAuthFlow) {
    return null;
  }

  await userManager.removeUser();
  const cookieDomain = getAppCookieDomain();
  document.cookie = `${appCookieName}=; max-age=0; path=/; domain=${cookieDomain}; samesite=strict; secure`;

  // Store error details for display on homepage
  if (errorContext) {
    let errorMessage = "";

    // Try to get detailed error from response body
    if (errorContext.response) {
      try {
        const errorData = await errorContext.response.clone().json();
        // Use backend error message if available
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Failed to parse JSON, use default messages
      }
    }

    // Fallback to generic messages based on status code
    if (!errorMessage) {
      if (errorContext.status === 401) {
        errorMessage = "Your session has expired. Please log in again.";
      } else if (errorContext.status === 403) {
        errorMessage =
          "Access denied. Your account may be missing required information. Please contact support if this persists.";
      } else {
        errorMessage = "Authentication error. Please try logging in again.";
      }
    }

    localStorage.setItem("firebase_loginError", errorMessage);
  }

  window.location.href = "/";
  return null;
};

// Ensures getUser only runs one instance at a time and returns the same promise to all callers
let getUserPromise: Promise<User | null> | null = null;

const getUser = async () => {
  if (getUserPromise) {
    return getUserPromise;
  }
  getUserPromise = (async () => {
    const user = await userManager.getUser();
    return user;
  })();

  try {
    return await getUserPromise;
  } finally {
    getUserPromise = null;
  }
};

// Monkey patch 'fetch' to add the authorization header to API requests
const { fetch: originalFetch } = window;
window.fetch = async (...args) => {
  const [resource, config = {}] = args;
  const ourAPI = isResourceOurAPI(resource);
  if (ourAPI) {
    let accessToken;

    // First try and get an access token from a user we have logged in
    const user = await getUser();
    if (user) {
      accessToken = user.access_token;
    }

    // Then see if we can get one from a Cookie (app webview)
    if (!accessToken && document.cookie) {
      accessToken = getCookie(appCookieName);
    }

    // If we have one then pass it in the appropriate header. We have to jump
    // through a few hoops to do this due to various nuances of fetch args
    if (accessToken) {
      // Use Authorization header for compatibility with both Firebase and OIDC backends
      const header = "Authorization";
      const bearer = `Bearer ${accessToken}`;
      const resourceIsRequest = resource instanceof Request;
      if (resourceIsRequest) {
        resource.headers.append(header, bearer);
      }
      if (!resourceIsRequest || config.headers) {
        if (config.headers instanceof Headers) {
          config.headers.append(header, bearer);
        } else {
          config.headers = {
            ...config.headers,
            [header]: bearer,
          };
        }
      }
    }
  }

  const response = await originalFetch(resource, config);
  if (ourAPI) {
    const resourceUrl =
      resource instanceof Request ? resource.url : resource.toString();
    const url = new URL(resourceUrl, window.location.origin);
    const isLogoutEndpoint = url.pathname === "/api/player/logout/";

    if ([401, 403].includes(response.status) && !isLogoutEndpoint) {
      await handleAuthIssue({
        status: response.status,
        url: url.pathname,
        response,
      });
    }
  }
  return response;
};

interface D3AuthProviderProps {
  children: ReactNode;
}

const D3AuthProvider: React.FC<D3AuthProviderProps> = ({ children }) => {
  const onSigninCallback = async () => {
    // Clean up URL hash after successful auth
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  return (
    <AuthProvider
      userManager={userManager}
      onSigninCallback={onSigninCallback}
      // Skip automatic signin callback - we handle it manually via backend
      skipSigninCallback={true}
    >
      {children}
    </AuthProvider>
  );
};

export default D3AuthProvider;
